import { Listing, ListingTemplate, LocationModel } from "@truckdown/locations";
import context from '@truckdown/components-systems';
import { defineComponent, ref, Ref, computed, PropType, watch } from "vue";
import { deepClone } from "../../utilities";

interface IListingViewModel {
    listingTemplateType: ListingTemplate | undefined,
    overrideDefault: boolean,
    selectedSite: string
};

interface IData {
    listingViewModel: Ref<IListingViewModel>,
    listingViewModelOriginal: Ref<IListingViewModel>,
    activeTemplate: Ref<ListingTemplate>
};

const getData = function (props: any) {
    let template = getTemplateType(props.location.subscriptions[context.system]?.tagName)
    let viewModel = {
        listingTemplateType: template,
        overrideDefault: false,
        selectedSite: context.system
    } as IListingViewModel;
    return {
        listingViewModel: ref<IListingViewModel>(viewModel),
        listingViewModelOriginal: ref<IListingViewModel>(deepClone(viewModel)),
        activeTemplate: ref<ListingTemplate>(template)
    } as IData;
}

function getTemplateType(input?: string): ListingTemplate {
    if (!input) return ListingTemplate.Owned;

    switch (input.replace(/#/g, "").toLowerCase()) {
        case "stock":
            return ListingTemplate.Stock;
        case "branded":
            return ListingTemplate.Branded;
        case "pro":
            return ListingTemplate.Pro;
        case "elite":
            return ListingTemplate.Elite;
        case "tdpreferred":
        case "td-preferred":
        case "td preferred":
            return ListingTemplate.TDPreferred;

        default:
            return ListingTemplate.Owned;
    }
}

const getMethods = function (data: IData, props: any, ctx: any) {
    return {
        toggleViewSelection: function () {
            ctx.emit('toggle-view-selection');
        },
        updateView: function () {
            ctx.emit('update-view', data.listingViewModel.value);
        },
        selectSite: function () {
            let currentSubType = props.location.subscriptions[data.listingViewModel.value.selectedSite]?.tagName ?? undefined;
            let template = getTemplateType(currentSubType);
            data.listingViewModel.value.listingTemplateType = template;
            data.activeTemplate.value = template;
            this.updateView();
        },
        selectTemplate: function () {
            this.updateView();
        },
        cancelViewSelection: function () {
            ctx.emit('update-view', data.listingViewModelOriginal.value);
            this.toggleViewSelection();
        },
        submitViewSelection: function () {
            ctx.emit('update-view', data.listingViewModel.value);
            this.toggleViewSelection();
        }
    }
}

const getComputed = function (props: any, data: IData) {
    return {
        siteName: computed(() => {
            if (data.listingViewModel.value.selectedSite === "truckdown") return "TruckDown";
            if (data.listingViewModel.value.selectedSite === "planrv") return "PlanRV";
            return null;
        }),
        showAppplyAllCheckbox: computed(() => {
            const details = props.location?.details ?? {};
            if (Object.keys(details).length === 0) return false;
            const firstKey = Object.keys(details)[0];
            return firstKey !== '';
        }),
        hasPlanrvSub: computed(() => {
             return props.location?.subscriptions?.planrv !== undefined;
        })
    };
}


export default defineComponent({
    name: "ViewSelection",
    props: {
        location: Object as () => LocationModel,
        editable: {
            type: Boolean,
            default: false
        },
        isAdmin: {
            type: Boolean,
            default: false,
            required: false
        }
    },
    emits: ['update-view', 'toggle-view-selection'],
    setup: function (props, ctx) {
        const data = getData(props);
        const methods = getMethods(data, props, ctx);
        const computed = getComputed(props, data);

        watch(
            () => props.location?.details,
            (newDetails, oldDetails) => {
                if (!oldDetails) return;

                const oldHadDefault = !!oldDetails[""];
                const newHasDefault = !!newDetails?.[""];

                // Only act when "" existed before but no longer exists
                if (oldHadDefault && !newHasDefault) {
                    console.log("default details removed - switching out of override mode");

                    data.listingViewModel.value.overrideDefault = false;
                    ctx.emit("update-view", data.listingViewModel.value);
                }
            },
            { deep: true }
        );


        return {
            ...data,
            ...methods,
            ...computed,
            'ListingTemplate': ListingTemplate
        };
    }
});